import React, { useState } from 'react';
import { FileSpreadsheet, Sparkles, Wand2, LayoutTemplate, Clock } from 'lucide-react';
import { WizardLayout, AutoSaveIndicator } from './WizardLayout';
import { hasEnoughDataForDetection } from '../../utils/financialDataNormalizer';
import EditablePLTable from '../EditablePLTable';
import DataGapDetector from '../DataGapDetector';
import PLVisualization from '../PLVisualization';
import TemplateSelector from '../TemplateSelector';
import SmartUploader from './SmartUploader';
import UnifiedUploader from './UnifiedUploader';
import TransactionViewer from '../TransactionViewer';
import Step3_DataEntry_Working from './Step3_DataEntry_Working';
import Step3_AI_Assistant from './Step3_AI_Assistant';
import EventCoveragePeriod from './EventCoveragePeriod';
import AnomalyBanner from './AnomalyBanner';
// QuickBooks integration disabled until Intuit app approval
// import QuickBooksConnect from '../QuickBooksConnect';

interface Step3Props {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  claimId?: string;  // Report/claim ID to link uploaded files
}

// Helper to update nested data
const onDataChange = (newData: any, data: any, onDataUpdate?: (d: any) => void) => {
  if (onDataUpdate) {
    onDataUpdate({ ...data, ...newData });
  }
};

export default function Step3({ onNext, onBack, data, onDataUpdate, onSave, claimId }: Step3Props) {
  // Auto-select mode based on whether data came from bulk spreadsheet upload or cleanup
  const initialMode = data.fromSpreadsheet ? 'ai' : (data.fromCleanup ? 'cleanup_complete' : (data.fromQuickBooks ? 'quickbooks_complete' : (data.fromTemplate ? 'template_complete' : (data.fromUnifiedUpload ? 'unified_complete' : null))));
  const [entryMode, setEntryMode] = useState<'csv' | 'ai' | 'unified' | 'unified_complete' | 'cleanup_complete' | 'quickbooks' | 'quickbooks_complete' | 'template' | 'template_complete' | null>(initialMode);
  const [aiExtractedData, setAiExtractedData] = useState<any>(
    data.fromSpreadsheet || data.fromCleanup || data.fromUnifiedUpload ? data.financial_data : null
  );
  const [unifiedUploadData, setUnifiedUploadData] = useState<any>(data.unified_upload_data || null);
  const [tax1065Files, setTax1065Files] = useState<any[]>(data.tax1065Files || []);
  const [userPLOverrides, setUserPLOverrides] = useState<Record<string, Record<string, number>>>({});
  const [plFiles, setPlFiles] = useState<any[]>(data.plFiles || []);
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>(data.uploadedDocuments || []);
  
  // Template state
  const [appliedTemplate, setAppliedTemplate] = useState<any>(data.applied_template || null);
  
  // Event coverage period state (moved from Step 2)
  const [eventStartDate, setEventStartDate] = useState(data.event_start_date || '');
  const [eventEndDate, setEventEndDate] = useState(data.event_end_date || '');
  const [femaDeclarationNumber, setFemaDeclarationNumber] = useState(data.fema_declaration_number || '');
  
  // Validation error/status state (replaces alert() calls)
  const [validationError, setValidationError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Anomaly -> Transaction linking state
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | null>(null);
  
  // Resolved anomalies state (persisted in wizard data)
  const [resolvedAnomalies, setResolvedAnomalies] = useState<Array<{id: string; note: string; resolved_at: string; anomaly_type?: string; deviation_percent?: number; related_amount?: number}>>(
    data.resolved_anomalies || []
  );
  
  // Cached AI anomaly analysis (persisted in wizard data)
  const [cachedAnomalyAnalysis, setCachedAnomalyAnalysis] = useState<{
    anomalies: any[];
    dataHash: string;
    analyzedAt: string;
  } | null>(data.cached_anomaly_analysis || null);
  
  // Handler for resolving anomalies
  const handleResolveAnomaly = (anomalyId: string, note: string) => {
    const newResolved = [...resolvedAnomalies, { 
      id: anomalyId, 
      note, 
      resolved_at: new Date().toISOString() 
    }];
    setResolvedAnomalies(newResolved);
    // Persist to wizard data for auto-save
    if (onDataUpdate) {
      onDataUpdate({ resolved_anomalies: newResolved });
    }
  };
  
  // Handler for unresolving anomalies
  const handleUnresolveAnomaly = (anomalyId: string) => {
    const newResolved = resolvedAnomalies.filter(r => r.id !== anomalyId);
    setResolvedAnomalies(newResolved);
    if (onDataUpdate) {
      onDataUpdate({ resolved_anomalies: newResolved });
    }
  };
  
  // Handler for saving AI analysis to wizard data
  const handleAnalysisComplete = (analysis: { anomalies: any[]; dataHash: string; analyzedAt: string }) => {
    setCachedAnomalyAnalysis(analysis);
    if (onDataUpdate) {
      onDataUpdate({ cached_anomaly_analysis: analysis });
    }
  };
  
  // Handler for event date changes
  const handleEventDateChange = (field: 'event_start_date' | 'event_end_date' | 'fema_declaration_number', value: string) => {
    if (field === 'event_start_date') setEventStartDate(value);
    else if (field === 'event_end_date') setEventEndDate(value);
    else if (field === 'fema_declaration_number') setFemaDeclarationNumber(value);
    setValidationError(null);
  };
  
  // Check if we have enough financial data for auto-detection (uses unified normalizer)
  const hasFinancialData = (plFiles.length > 0) || 
    hasEnoughDataForDetection(aiExtractedData, plFiles) ||
    hasEnoughDataForDetection(data.financial_data, data.plFiles);

  // Log when data comes from bulk upload or cleanup
  React.useEffect(() => {
    if (data.fromSpreadsheet) {
      console.log('📊 Step3: Data loaded from spreadsheet bulk upload');
      console.log('   financial_data keys:', Object.keys(data.financial_data || {}));
      console.log('   tax1065Files:', data.tax1065Files?.length || 0);
    }
    if (data.fromCleanup) {
      console.log('🧹 Step3: Data loaded from BI Cleanup');
      console.log('   financial_data keys:', Object.keys(data.financial_data || {}));
      console.log('   cleanup_job_id:', data.cleanup_job_id);
      console.log('   cleanup_summary:', data.cleanup_summary);
    }
  }, [data.fromSpreadsheet, data.fromCleanup, data.financial_data, data.tax1065Files, data.cleanup_job_id, data.cleanup_summary]);

  // State to store files from SmartUploader for seamless handoff
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // If mode not selected, show Unified Uploader (new default) wrapped in consistent layout
  if (!entryMode) {
    return (
      <WizardLayout
        title="Financial Data"
        subtitle="Upload your business financial documents for AI-powered analysis"
        timeEstimate="10-15 minutes"
        showAutoSave={false}
      >
        <div className="-mx-8 -mb-6">
          <UnifiedUploader
            reportId={claimId || data?.id || data?.draft_id || data?.report_id}
            onComplete={(result) => {
          console.log('📦 UnifiedUploader complete:', result.stats);
          console.log(`📎 Linked to claim: ${claimId || data?.id || 'none'}`);
          
          // Convert canonical_data to financial_data format for wizard
          // SINGLE FORMAT: Only use data_YYYY format (Step 5 / BI compatible)
          if (result.canonical_data) {
            const yearlyData: Record<string, any[]> = {};
            const years: number[] = [];
            
            // Convert periods to data_YYYY format ONLY
            result.canonical_data.periods?.forEach((period: any) => {
              const year = period.year;
              const month = period.month || 1;
              
              // Track unique years
              if (!years.includes(year)) {
                years.push(year);
              }
              
              // Initialize year array if needed
              const yearKey = `data_${year}`;
              if (!yearlyData[yearKey]) {
                yearlyData[yearKey] = [];
              }
              
              // Add monthly data to the year array
              yearlyData[yearKey].push({
                year: year,
                month: month,
                revenue: period.gross_revenue || 0,
                cogs: period.cogs || 0,
                payroll: (period.employee_wages || 0) + (period.officer_compensation || 0),
                employee_wages: period.employee_wages || 0,
                officer_compensation: period.officer_compensation || 0,
                fixed_costs: period.fixed_costs || 0,
                variable_costs: period.variable_costs || 0,
                net_profit: period.net_profit || 0,
                line_items: period.line_items || {},
                // Preserve transaction count for reference
                transaction_count: period.transactions?.length || 0,
              });
            });
            
            // Sort years
            years.sort((a, b) => a - b);
            
            // Sort monthly data within each year by month
            Object.keys(yearlyData).forEach(yearKey => {
              yearlyData[yearKey].sort((a: any, b: any) => a.month - b.month);
            });
            
            // SINGLE FORMAT: Only data_YYYY keys
            const financialData: Record<string, any> = {
              ...yearlyData,  // Only data_YYYY format
            };
            
            console.log('📊 Converted financial_data (SINGLE FORMAT):');
            console.log('   Years detected:', years);
            console.log('   Keys:', Object.keys(financialData));
            console.log('   Sample data_' + years[0] + ':', yearlyData[`data_${years[0]}`]?.length, 'months');
            
            // Store unified upload data for review
            setUnifiedUploadData(result);
            setAiExtractedData(financialData);
            
            // ✅ CRITICAL FIX: Extract and store tax1065Files from upload result
            if (result.tax1065Files && result.tax1065Files.length > 0) {
              console.log('📋 Tax forms detected:', result.tax1065Files.length, 'files');
              result.tax1065Files.forEach((file: any) => {
                console.log(`   - ${file.filename} (${file.form_type})`);
                console.log(`     data.tax_year: ${file.data?.tax_year}`);
                console.log(`     data.ordinary_income: ${file.data?.ordinary_income}`);
                console.log(`     data.partner_name: ${file.data?.partner_name}`);
              });
              setTax1065Files(result.tax1065Files);
            } else {
              console.log('⚠️ No tax1065Files in upload result');
            }
            
            // Build payroll_data structure for Step 5 compatibility
            const payrollData: Record<string, any> = {};
            years.forEach(year => {
              const yearData = yearlyData[`data_${year}`] || [];
              const totalPayroll = yearData.reduce((sum: number, m: any) => sum + (m.payroll || 0), 0);
              const totalEmployeeWages = yearData.reduce((sum: number, m: any) => sum + (m.employee_wages || 0), 0);
              const totalOfficerComp = yearData.reduce((sum: number, m: any) => sum + (m.officer_compensation || 0), 0);
              
              payrollData[year] = {
                total: totalPayroll,
                employee_wages: totalEmployeeWages,
                officer_compensation: totalOfficerComp,
              };
            });
            
            // Update wizard data with calculated_years and payroll_data for downstream steps
            if (onDataUpdate) {
              const updatedData: Record<string, any> = {
                ...data,
                financial_data: financialData,
                fromUnifiedUpload: true,
                unified_upload_data: result,
                calculated_years: years,
                payroll_data: payrollData,
                // ✅ CRITICAL: Include tax1065Files in wizard state
                tax1065Files: result.tax1065Files || [],
              };
              
              // Also add individual year payroll fields for Step 5 compatibility
              years.forEach(year => {
                updatedData[`payroll_${year}`] = payrollData[year]?.total || 0;
                updatedData[`employee_payroll_${year}`] = payrollData[year]?.employee_wages || 0;
                updatedData[`officer_compensation_${year}`] = payrollData[year]?.officer_compensation || 0;
              });
              
              console.log('📊 Payroll data for Step 5:', payrollData);
              console.log('📋 Tax1065 files for downstream:', result.tax1065Files?.length || 0);
              
              // ✅ CRITICAL: Merge with existing data and save immediately
              const fullDataToSave = {
                ...data,
                ...updatedData,
              };
              
              console.log('💾 Full data to save has tax1065Files:', fullDataToSave.tax1065Files?.length || 0);
              
              // Update parent state
              onDataUpdate(updatedData);
              
              // ✅ CRITICAL FIX: Pass explicit data to save to avoid stale ref issue
              if (onSave) {
                console.log('💾 Triggering save with EXPLICIT data...');
                // Use a longer delay and pass the merged data
                setTimeout(async () => {
                  try {
                    // @ts-ignore - onSave can accept explicit data
                    await onSave(fullDataToSave);
                    console.log('✅ Save completed with', fullDataToSave.tax1065Files?.length || 0, 'tax files');
                  } catch (err) {
                    console.error('❌ Save failed:', err);
                  }
                }, 100);
              }
            }
            
            // Move to unified_complete mode to show review
            setEntryMode('unified_complete');
          } else {
            // Fallback to legacy mode
            setEntryMode('csv');
          }
        }}
        onBack={onBack}
        wizardData={data}
      />
        </div>
      </WizardLayout>
    );
  }

  // Unified Upload Complete - show review before proceeding
  if (entryMode === 'unified_complete') {
    const stats = unifiedUploadData?.stats || {};
    const hasReviewItems = (unifiedUploadData?.review_items?.length || 0) > 0;
    
    // Check if we have ANY useful data to show
    const hasFinancialData = aiExtractedData && Object.keys(aiExtractedData).length > 0;
    const hasTaxFiles = tax1065Files.length > 0 || (data.tax1065Files?.length || 0) > 0;
    const hasPlFiles = plFiles.length > 0 || (data.plFiles?.length || 0) > 0;
    
    // If no data at all, show upload option
    if (!hasFinancialData && !hasTaxFiles && !hasPlFiles) {
      return (
        <WizardLayout
          title="Upload Your Documents"
          subtitle="This case needs financial documents to continue."
          showAutoSave={false}
        >
          <div className="-mx-8 -mb-6">
            <UnifiedUploader
              reportId={claimId || data?.id || data?.draft_id || data?.report_id}
              onComplete={(result) => {
                console.log('📦 UnifiedUploader complete (re-upload):', result.stats);
                console.log(`📎 Linked to claim: ${claimId || data?.id || 'none'}`);
                
                // Convert canonical_data to financial_data format
                if (result.canonical_data) {
                  const yearlyData: Record<string, any[]> = {};
                  const years: number[] = [];
                  
                  result.canonical_data.periods?.forEach((period: any) => {
                    const year = period.year;
                    const month = period.month || 1;
                    
                    if (!years.includes(year)) years.push(year);
                    
                    const yearKey = `data_${year}`;
                    if (!yearlyData[yearKey]) yearlyData[yearKey] = [];
                    
                    yearlyData[yearKey].push({
                      year, month,
                      revenue: period.gross_revenue || 0,
                      cogs: period.cogs || 0,
                      payroll: (period.employee_wages || 0) + (period.officer_compensation || 0),
                      employee_wages: period.employee_wages || 0,
                      officer_compensation: period.officer_compensation || 0,
                      fixed_costs: period.fixed_costs || 0,
                      variable_costs: period.variable_costs || 0,
                      net_profit: period.net_profit || 0,
                      line_items: period.line_items || {},
                      transaction_count: period.transactions?.length || 0,
                    });
                  });
                  
                  setAiExtractedData(yearlyData);
                  setUnifiedUploadData(result);
                  setTax1065Files(result.tax1065Files || []);
                  
                  // Update parent state
                  if (onDataUpdate) {
                    onDataUpdate({
                      financial_data: yearlyData,
                      fromUnifiedUpload: true,
                      unified_upload_data: result,
                      tax1065Files: result.tax1065Files || [],
                    });
                  }
                  
                  // Auto-save
                  if (onSave) setTimeout(() => onSave(), 500);
                }
              }}
              onCancel={() => onBack()}
            />
          </div>
        </WizardLayout>
      );
    }
    
    return (
      <WizardLayout
        title="Data Uploaded Successfully"
        subtitle="Review your extracted data before proceeding"
        showAutoSave={false}
      >
        <div className="space-y-4">
          {/* Stats Summary - Compact */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-2 bg-blue-50 rounded">
                <div className="text-xl font-bold text-blue-900">{stats.total_transactions || 0}</div>
                <div className="text-xs text-blue-600">Transactions</div>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <div className="text-xl font-bold text-green-900">{stats.total_periods || 0}</div>
                <div className="text-xs text-green-600">Periods</div>
              </div>
              <div className="text-center p-2 bg-purple-50 rounded">
                <div className="text-xl font-bold text-purple-900">{stats.files_processed || 0}</div>
                <div className="text-xs text-purple-600">Files</div>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded">
                <div className="text-xl font-bold text-amber-900">
                  {/* Calculate needs_review from transactions using frontend thresholds */}
                  {(() => {
                    if (!unifiedUploadData?.canonical_data?.periods) return stats.items_needing_review || 0;
                    let needsReview = 0;
                    const CLEAR_CUT = new Set(['Utilities', 'Rent & Occupancy', 'Rent', 'Payroll & Benefits', 'Payroll', 'Insurance', 'Bank & Financial Fees', 'Sales Revenue', 'Revenue', 'COGS', 'Inventory & Merchandise', 'Food & Beverage Cost']);
                    for (const period of unifiedUploadData.canonical_data.periods) {
                      for (const txn of (period.transactions || [])) {
                        const conf = txn.classification?.confidence || 0;
                        const cat = txn.category || '';
                        const isAutoApproved = conf >= 0.95 || (CLEAR_CUT.has(cat) && conf >= 0.85);
                        if (!isAutoApproved) needsReview++;
                      }
                    }
                    return needsReview;
                  })()}
                </div>
                <div className="text-xs text-amber-600">Needs Review</div>
              </div>
            </div>
          </div>

          {/* P&L Preview - Full Width Forensic View */}
          {aiExtractedData && Object.keys(aiExtractedData).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h2 className="text-base font-semibold mb-3">Financial Data Summary</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2 px-3">Period</th>
                      <th className="text-right py-2 px-3">Revenue</th>
                      <th className="text-right py-2 px-3">Payroll</th>
                      <th className="text-right py-2 px-3">Fixed</th>
                      <th className="text-right py-2 px-3">Variable</th>
                      <th className="text-right py-2 px-3">COGS</th>
                      <th className="text-right py-2 px-3">Txns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Iterate over data_YYYY keys and flatten to monthly rows */}
                    {Object.entries(aiExtractedData)
                      .filter(([key]) => key.startsWith('data_'))
                      .sort(([a], [b]) => a.localeCompare(b))
                      .flatMap(([yearKey, months]: [string, any]) => 
                        (Array.isArray(months) ? months : []).map((m: any) => ({
                          ...m,
                          periodKey: `${m.year}-${String(m.month).padStart(2, '0')}`
                        }))
                      )
                      .sort((a, b) => a.periodKey.localeCompare(b.periodKey))
                      .map((m: any) => (
                        <tr key={m.periodKey} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium">{m.periodKey}</td>
                          <td className="text-right px-3 text-green-600">${(m.revenue || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                          <td className="text-right px-3 text-blue-600">${(m.payroll || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                          <td className="text-right px-3 text-orange-600">${(m.fixed_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                          <td className="text-right px-3 text-purple-600">${(m.variable_costs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                          <td className="text-right px-3 text-red-600">${(m.cogs || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                          <td className="text-right px-3 text-gray-500">{m.transaction_count || 0}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 🔍 ANOMALY DETECTION - Above Transaction Viewer */}
          {unifiedUploadData?.canonical_data && (
            <AnomalyBanner 
              canonicalData={unifiedUploadData.canonical_data}
              resolvedAnomalies={resolvedAnomalies}
              businessContext={{
                business_type: data.business_type,
                industry: data.naics_code,
                event_type: data.event_type,
                event_start: eventStartDate,
                event_end: eventEndDate
              }}
              enableAIAnalysis={true}
              cachedAnalysis={cachedAnomalyAnalysis}
              onAnalysisComplete={handleAnalysisComplete}
              onViewTransaction={(txnId) => {
                console.log('View transaction from anomaly:', txnId);
                setHighlightedTransactionId(txnId);
              }}
              onResolveAnomaly={handleResolveAnomaly}
              onUnresolveAnomaly={handleUnresolveAnomaly}
            />
          )}

          {/* Transaction Detail View - Forensic Accounting */}
          {unifiedUploadData?.canonical_data?.periods && (
            <div className="mb-6">
              <TransactionViewer 
                periods={unifiedUploadData.canonical_data.periods}
                highlightedTransactionId={highlightedTransactionId}
                onHighlightClear={() => setHighlightedTransactionId(null)}
                onTransactionEdit={(periodKey, txnIndex, updates) => {
                  console.log('Transaction edit:', periodKey, txnIndex, updates);
                  
                  // Update the transaction in the unified upload data
                  setUnifiedUploadData((prev: any) => {
                    if (!prev?.canonical_data?.periods) return prev;
                    
                    const newPeriods = prev.canonical_data.periods.map((period: any) => {
                      const pKey = `${period.year}-${String(period.month).padStart(2, '0')}`;
                      if (pKey !== periodKey) return period;
                      
                      const newTransactions = [...(period.transactions || [])];
                      if (newTransactions[txnIndex]) {
                        newTransactions[txnIndex] = {
                          ...newTransactions[txnIndex],
                          ...updates,
                          // Mark as approved if validation_status is 'approved'
                          needs_review: updates.needs_review ?? newTransactions[txnIndex].needs_review,
                        };
                      }
                      
                      return { ...period, transactions: newTransactions };
                    });
                    
                    return {
                      ...prev,
                      canonical_data: {
                        ...prev.canonical_data,
                        periods: newPeriods,
                      },
                    };
                  });
                }}
                onBulkApprove={(transactionIds) => {
                  console.log('Bulk approve:', transactionIds.length, 'transactions');
                  
                  // Bulk update all selected transactions
                  setUnifiedUploadData((prev: any) => {
                    if (!prev?.canonical_data?.periods) return prev;
                    
                    const idSet = new Set(transactionIds);
                    
                    const newPeriods = prev.canonical_data.periods.map((period: any) => {
                      const newTransactions = (period.transactions || []).map((txn: any, idx: number) => {
                        const txnId = `${period.year}-${String(period.month).padStart(2, '0')}-${idx}`;
                        if (idSet.has(txnId)) {
                          return {
                            ...txn,
                            validation_status: 'approved',
                            needs_review: false,
                          };
                        }
                        return txn;
                      });
                      
                      return { ...period, transactions: newTransactions };
                    });
                    
                    return {
                      ...prev,
                      canonical_data: {
                        ...prev.canonical_data,
                        periods: newPeriods,
                      },
                    };
                  });
                }}
              />
            </div>
          )}

          {/* 📋 TAX RECORDS SECTION - Review K-1/1065 data before proceeding */}
          {(tax1065Files.length > 0 || data.tax1065Files?.length > 0) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <span>📋</span> Tax Records
                  <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold">
                    {(tax1065Files.length || data.tax1065Files?.length || 0)} Form{(tax1065Files.length || data.tax1065Files?.length || 0) > 1 ? 's' : ''}
                  </span>
                </h2>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                Review extracted tax data below. If data is missing or incorrect, re-upload the forms.
              </p>
              
              {(() => {
                const taxFiles = tax1065Files.length > 0 ? tax1065Files : (data.tax1065Files || []);
                const form1065s = taxFiles.filter((f: any) => f.form_type === '1065' || f.data?.form_type === '1065');
                const k1Forms = taxFiles.filter((f: any) => {
                  const formType = f.form_type || f.data?.form_type;
                  return formType === 'K-1' || formType === 'K1' || formType?.toLowerCase() === 'k-1';
                });
                
                return (
                  <div className="space-y-3">
                    {/* Form 1065 Records */}
                    {form1065s.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="font-semibold text-blue-900 mb-2 text-sm flex items-center gap-2">
                          <span>📄</span> Form 1065 - Partnership Return ({form1065s.length})
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-blue-100">
                              <tr>
                                <th className="text-left p-2 font-semibold text-blue-900">Year</th>
                                <th className="text-right p-2 font-semibold text-blue-900">Gross Receipts</th>
                                <th className="text-right p-2 font-semibold text-blue-900">COGS</th>
                                <th className="text-right p-2 font-semibold text-blue-900">Salaries/Wages</th>
                                <th className="text-right p-2 font-semibold text-blue-900">Guaranteed Pay</th>
                              </tr>
                            </thead>
                            <tbody>
                              {form1065s.map((file: any, idx: number) => {
                                const taxData = file.data || {};
                                const grossReceipts = taxData.gross_receipts || 0;
                                const cogs = taxData.cost_of_goods_sold || 0;
                                const salariesWages = taxData.salaries_wages || 0;
                                const guaranteedPayments = taxData.guaranteed_payments || 0;
                                
                                return (
                                  <tr key={idx} className="border-t border-blue-200">
                                    <td className="p-2 font-mono font-bold text-blue-800">
                                      {taxData.tax_year || file.year || '-'}
                                    </td>
                                    <td className={`p-2 text-right font-mono ${grossReceipts === 0 ? 'text-red-500' : 'text-gray-700'}`}>
                                      ${grossReceipts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      {grossReceipts === 0 && <span className="ml-1 text-red-500">⚠️</span>}
                                    </td>
                                    <td className="p-2 text-right font-mono text-gray-700">
                                      ${cogs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className={`p-2 text-right font-mono font-bold ${salariesWages === 0 ? 'text-orange-500' : 'text-blue-700'}`}>
                                      ${salariesWages.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="p-2 text-right font-mono text-gray-700">
                                      ${guaranteedPayments.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-2 p-2 bg-blue-100 rounded text-xs text-blue-800">
                          <strong>Form 1065:</strong> Salaries/Wages (Line 9) = Employee payroll used in VP calculations.
                        </div>
                      </div>
                    )}
                    
                    {/* Schedule K-1 Records */}
                    {k1Forms.length > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <h4 className="font-semibold text-green-900 mb-2 text-sm flex items-center gap-2">
                          <span>📑</span> Schedule K-1 - Partner's Share ({k1Forms.length})
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-green-100">
                              <tr>
                                <th className="text-left p-2 font-semibold text-green-900">Year</th>
                                <th className="text-left p-2 font-semibold text-green-900">Partner</th>
                                <th className="text-right p-2 font-semibold text-green-900">Box 1: Ordinary</th>
                                <th className="text-right p-2 font-semibold text-green-900">Box 4: Guaranteed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {k1Forms.flatMap((file: any, idx: number) => {
                                const taxData = file.data || {};
                                const scheduleK1 = taxData.schedule_k1 || {};
                                
                                if (scheduleK1.partners && scheduleK1.partners.length > 0) {
                                  return scheduleK1.partners.map((partner: any, pIdx: number) => (
                                    <tr key={`${idx}-${pIdx}`} className="border-t border-green-200">
                                      <td className="p-2 font-mono font-bold text-green-800">
                                        {taxData.tax_year || file.year || '-'}
                                      </td>
                                      <td className="p-2 text-gray-700">
                                        {partner.name || 'Unknown'}
                                      </td>
                                      <td className={`p-2 text-right font-mono font-bold ${(partner.box_1_ordinary_income || 0) === 0 ? 'text-red-500' : 'text-green-700'}`}>
                                        ${(partner.box_1_ordinary_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        {(partner.box_1_ordinary_income || 0) === 0 && <span className="ml-1">⚠️</span>}
                                      </td>
                                      <td className="p-2 text-right font-mono">
                                        ${(partner.box_4_guaranteed_payments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                    </tr>
                                  ));
                                }
                                
                                const ordinaryIncome = taxData.ordinary_income || scheduleK1.box_1_ordinary_income || 0;
                                return [(
                                  <tr key={idx} className="border-t border-green-200">
                                    <td className="p-2 font-mono font-bold text-green-800">
                                      {taxData.tax_year || file.year || '-'}
                                    </td>
                                    <td className="p-2 text-gray-700">
                                      {taxData.partner_name || 'Unknown'}
                                    </td>
                                    <td className={`p-2 text-right font-mono font-bold ${ordinaryIncome === 0 ? 'text-red-500' : 'text-green-700'}`}>
                                      ${ordinaryIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      {ordinaryIncome === 0 && <span className="ml-1">⚠️</span>}
                                    </td>
                                    <td className="p-2 text-right font-mono">
                                      ${(taxData.guaranteed_payments || scheduleK1.box_4_guaranteed_payments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                )];
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Warning if no data extracted */}
                    {form1065s.length === 0 && k1Forms.length === 0 && (
                      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                        <p className="text-yellow-800 text-sm font-semibold">⚠️ Tax files uploaded but form type not detected</p>
                        <p className="text-yellow-700 text-xs mt-1">
                          Files may need to be re-uploaded or manually classified as 1065 or K-1.
                        </p>
                      </div>
                    )}
                    
                    {/* Summary */}
                    {(form1065s.length > 0 || k1Forms.length > 0) && (() => {
                      let totalGrossReceipts = 0;
                      let totalSalariesWages = 0;
                      let totalOrdinaryK1 = 0;
                      
                      form1065s.forEach((f: any) => {
                        const d = f.data || {};
                        totalGrossReceipts += d.gross_receipts || 0;
                        totalSalariesWages += d.salaries_wages || 0;
                      });
                      
                      k1Forms.forEach((f: any) => {
                        const d = f.data || {};
                        if (d.schedule_k1?.partners) {
                          d.schedule_k1.partners.forEach((p: any) => {
                            totalOrdinaryK1 += p.box_1_ordinary_income || 0;
                          });
                        } else {
                          totalOrdinaryK1 += d.ordinary_income || d.schedule_k1?.box_1_ordinary_income || 0;
                        }
                      });
                      
                      const hasIssues = (form1065s.length > 0 && totalGrossReceipts === 0) || 
                                        (k1Forms.length > 0 && totalOrdinaryK1 === 0);
                      
                      return (
                        <div className={`border rounded-lg p-3 ${hasIssues ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className={`text-sm font-semibold ${hasIssues ? 'text-red-800' : 'text-amber-800'}`}>
                              {hasIssues ? '⚠️ Data Extraction Issue' : '📊 Tax Summary'}
                            </span>
                            <div className="text-xs space-x-4">
                              {form1065s.length > 0 && (
                                <>
                                  <span>Gross Receipts: <strong>${totalGrossReceipts.toLocaleString()}</strong></span>
                                  <span>Salaries/Wages: <strong className={totalSalariesWages === 0 ? 'text-orange-600' : 'text-blue-700'}>${totalSalariesWages.toLocaleString()}</strong></span>
                                </>
                              )}
                              {k1Forms.length > 0 && (
                                <span>K-1 Income: <strong className={totalOrdinaryK1 === 0 ? 'text-red-600' : 'text-green-700'}>${totalOrdinaryK1.toLocaleString()}</strong></span>
                              )}
                            </div>
                          </div>
                          {hasIssues && (
                            <p className="text-xs text-red-700 mt-2">
                              Some key values are $0. Check if the PDF was readable or re-upload clearer copies.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Event Coverage Period with Auto-Detection */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-base font-semibold mb-3">📅 Event Coverage Period</h2>
            {(() => {
              console.log('🔍 EventCoveragePeriod props:');
              console.log('   aiExtractedData:', aiExtractedData ? Object.keys(aiExtractedData) : 'null');
              console.log('   data.financial_data:', data.financial_data ? Object.keys(data.financial_data) : 'null');
              return null;
            })()}
            <EventCoveragePeriod
              financialData={aiExtractedData || data.financial_data}
              plFiles={[]}
              eventStartDate={eventStartDate}
              eventEndDate={eventEndDate}
              femaDeclarationNumber={femaDeclarationNumber}
              onDateChange={handleEventDateChange}
              businessAddress={data.business_address}
            />
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                // Reset to show uploader again
                setEntryMode(null);
                setUnifiedUploadData(null);
                setAiExtractedData(null);
              }}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
            >
              ← Re-upload Files
            </button>
            <button
              onClick={() => {
                // Extract years from the data for downstream steps
                const years = Object.keys(aiExtractedData || {})
                  .filter(key => key.startsWith('data_'))
                  .map(key => parseInt(key.replace('data_', '')))
                  .filter(y => !isNaN(y))
                  .sort((a, b) => a - b);
                
                console.log('📤 Passing to Step 4 with years:', years);
                console.log('📋 Passing tax1065Files:', tax1065Files?.length || 0);
                console.log('📋 tax1065Files content:', JSON.stringify(tax1065Files, null, 2));
                
                onNext({
                  ...data,
                  financial_data: aiExtractedData,
                  fromUnifiedUpload: true,
                  unified_upload_data: unifiedUploadData,
                  calculated_years: years,
                  // ✅ CRITICAL: Pass tax1065Files to downstream steps
                  tax1065Files: tax1065Files,
                });
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              Continue to Date Selection →
            </button>
          </div>
        </div>
      </WizardLayout>
    );
  }

  // Render selected mode
  if (entryMode === 'csv') {
    return <Step3_DataEntry_Working 
      onNext={(csvData) => onNext({ ...csvData, tax1065Files })} 
      onBack={() => { setPendingFiles([]); setEntryMode(null); }} 
      data={data}
      initialFiles={pendingFiles}  // Pass files for seamless handoff
    />;
  }

  // Industry Template Mode
  if (entryMode === 'template') {
    return (
      <WizardLayout
        title="Industry Templates"
        subtitle="Select your industry and enter revenue to generate P&L estimates"
        showAutoSave={false}
      >
        <TemplateSelector
          businessType={data.business_type}
          currentRevenue={data.annual_revenue || 0}
          coverageYear={eventStartDate ? new Date(eventStartDate).getFullYear() : new Date().getFullYear()}
          onApplyTemplate={(templateData) => {
            console.log('✅ Template applied:', templateData);
            setAppliedTemplate(templateData);
            
            // Update wizard data with template data
            if (onDataUpdate) {
              onDataUpdate({
                financial_data: templateData.financial_data,
                reconstructed_pl: templateData.reconstructed_pl,
                fromTemplate: true,
                applied_template: templateData,
                template_id: templateData.template_id,
                template_name: templateData.template_name
              });
            }
            
            // Switch to template_complete view
            setEntryMode('template_complete');
          }}
        />

        {/* Back button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setEntryMode(null)}
            className="text-gray-600 hover:text-gray-800 font-semibold"
          >
            ← Change Method
          </button>
        </div>
      </WizardLayout>
    );
  }

  // Template Complete Mode - Review and Continue
  if (entryMode === 'template_complete') {
    const template = appliedTemplate || data.applied_template;
    
    return (
      <WizardLayout
        title={`${template?.template_name || 'Industry Template'} Applied`}
        subtitle="P&L estimates generated based on industry benchmarks"
        showAutoSave={false}
      >
        <div className="space-y-6">
          {validationError && (
            <p className="text-red-500 text-sm">{validationError}</p>
          )}
          {/* Success Header */}
          <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-100">
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
              <span className="text-3xl">{template?.template_icon || '📊'}</span>
            </div>
            <div className="flex-1">
              <p className="text-green-700 font-medium flex items-center gap-2">
                <span className="text-green-500">✓</span> Template successfully applied
              </p>
            </div>
            <button
              onClick={() => setEntryMode('template')}
              className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition text-sm font-medium"
            >
              Change Template
            </button>
          </div>

          {/* Quick Stats */}
          {template?.reconstructed_pl?.totals && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Revenue</p>
                <p className="text-xl font-bold text-green-600">
                  ${(template.reconstructed_pl.totals.revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">COGS</p>
                <p className="text-xl font-bold text-amber-600">
                  ${(template.reconstructed_pl.totals.cogs || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Payroll</p>
                <p className="text-xl font-bold text-blue-600">
                  ${(template.reconstructed_pl.totals.payroll || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Net Income</p>
                <p className="text-xl font-bold text-blue-600">
                  ${(template.reconstructed_pl.totals.net_income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          )}

          {/* Template Defaults Used */}
          {template?.defaults_used && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Template Assumptions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-3 bg-white rounded-lg border">
                  <span className="text-gray-600">COGS:</span>
                  <span className="ml-2 font-semibold">{template.defaults_used.cogs_pct}%</span>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <span className="text-gray-600">Payroll:</span>
                  <span className="ml-2 font-semibold">{template.defaults_used.payroll_pct}%</span>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <span className="text-gray-600">Rent:</span>
                  <span className="ml-2 font-semibold">${template.defaults_used.rent_monthly?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</span>
                </div>
                <div className="p-3 bg-white rounded-lg border">
                  <span className="text-gray-600">Marketing:</span>
                  <span className="ml-2 font-semibold">{template.defaults_used.marketing_pct}%</span>
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-500">
                These are industry-standard estimates. You can override individual values in Step 4.
              </p>
            </div>
          )}

          {/* Industry Tips */}
          {template?.tips && template.tips.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-2">
                Industry Tips for {template.template_name}
              </h3>
              <ul className="space-y-1 text-sm text-amber-700">
                {template.tips.map((tip: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* P&L Visualization */}
          {(template?.reconstructed_pl || data.reconstructed_pl) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <PLVisualization
                financialData={template?.financial_data || data.financial_data || {}}
                reconstructedPL={template?.reconstructed_pl || data.reconstructed_pl}
                industryBenchmarks={template?.benchmarks}
                eventYear={template?.applied_to_year?.toString() || eventStartDate ? new Date(eventStartDate).getFullYear().toString() : undefined}
              />
            </div>
          )}

          {/* Event Coverage Period */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Event Coverage Period
              <span className="text-red-500 text-xs font-normal">(Required)</span>
            </h2>
            <EventCoveragePeriod
              financialData={template?.financial_data || data.financial_data}
              plFiles={[]}
              eventStartDate={eventStartDate}
              eventEndDate={eventEndDate}
              femaDeclarationNumber={femaDeclarationNumber}
              onDateChange={handleEventDateChange}
              businessAddress={data.business_address}
            />
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={() => setEntryMode('template')}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
            >
              ← Change Template
            </button>
            <button
              onClick={() => {
                // Validate event dates
                if (!eventStartDate || !eventEndDate) {
                  setValidationError('Please enter the Event Coverage Period dates before continuing.');
                  return;
                }

                const startDate = new Date(eventStartDate);
                const endDate = new Date(eventEndDate);
                if (endDate < startDate) {
                  setValidationError('Event end date must be after start date');
                  return;
                }

                const coverage_year = startDate.getFullYear();

                onNext({
                  method: 'template',
                  financial_data: template?.financial_data || data.financial_data,
                  reconstructed_pl: template?.reconstructed_pl || data.reconstructed_pl,
                  fromTemplate: true,
                  applied_template: template,
                  template_id: template?.template_id,
                  template_name: template?.template_name,
                  event_start_date: eventStartDate,
                  event_end_date: eventEndDate,
                  fema_declaration_number: femaDeclarationNumber,
                  coverage_year: coverage_year
                });
              }}
              disabled={!eventStartDate || !eventEndDate}
              className={`px-6 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                eventStartDate && eventEndDate
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue to Step 4 →
            </button>
          </div>
        </div>
      </WizardLayout>
    );
  }

  if (entryMode === 'ai') {
    return (
      <WizardLayout
        title="AI-Powered Data Entry"
        subtitle="Upload financial documents for intelligent extraction and analysis"
        showAutoSave={false}
      >
        <div className="space-y-6">
          {validationError && (
            <p className="text-red-500 text-sm">{validationError}</p>
          )}
          {/* AI Assistant Component */}
          <div className="-mx-8 -mt-6">
            <Step3_AI_Assistant 
              existingData={data}
              onSave={onSave}
              initialFiles={pendingFiles}
              onDataExtracted={(extractedData) => {
                console.log('🟢 Step3 wrapper: Received data from AI Assistant');
                setAiExtractedData(extractedData);
                if (extractedData.tax1065Files) setTax1065Files(extractedData.tax1065Files);
                if (extractedData.plFiles) setPlFiles(extractedData.plFiles);
                if (extractedData.uploadedDocuments) setUploadedDocuments(extractedData.uploadedDocuments);
                
                if (onDataUpdate) {
                  onDataUpdate({
                    plFiles: extractedData.plFiles || [],
                    uploadedDocuments: extractedData.uploadedDocuments || [],
                    tax1065Files: extractedData.tax1065Files || []
                  });
                }
              }} 
            />
          </div>
          
          {/* Event Coverage Period */}
          {hasFinancialData && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h2 className="text-base font-semibold mb-3">📅 Event Coverage Period</h2>
              <EventCoveragePeriod
                financialData={aiExtractedData || data.financial_data}
                plFiles={plFiles}
                eventStartDate={eventStartDate}
                eventEndDate={eventEndDate}
                femaDeclarationNumber={femaDeclarationNumber}
                onDateChange={handleEventDateChange}
                businessAddress={data.business_address}
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={() => { setPendingFiles([]); setEntryMode(null); }}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
            >
              ← Change Method
            </button>
            <button
              onClick={() => {
                if (!eventStartDate || !eventEndDate) {
                  setValidationError('Please enter the Event Coverage Period dates before continuing.');
                  return;
                }

                const startDate = new Date(eventStartDate);
                const endDate = new Date(eventEndDate);
                if (endDate < startDate) {
                  setValidationError('Event end date must be after start date');
                  return;
                }

                const tax1065FilesToPass = tax1065Files.length > 0 ? tax1065Files : (aiExtractedData?.tax1065Files || []);
                const plFilesToPass = plFiles.length > 0 ? plFiles : (aiExtractedData?.plFiles || []);
                const uploadedDocsToPass = uploadedDocuments.length > 0 ? uploadedDocuments : (aiExtractedData?.uploadedDocuments || []);
                
                const dataToPass: any = { 
                  method: 'ai',
                  plFiles: plFilesToPass,
                  tax1065Files: tax1065FilesToPass,
                  uploadedDocuments: uploadedDocsToPass,
                  event_start_date: eventStartDate,
                  event_end_date: eventEndDate,
                  fema_declaration_number: femaDeclarationNumber,
                  coverage_year: startDate.getFullYear()
                };
                
                if (aiExtractedData && Object.keys(aiExtractedData).length > 0) {
                  dataToPass.financial_data = aiExtractedData;
                }
                
                onNext(dataToPass);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              Continue to Step 4 →
            </button>
          </div>
        </div>
      </WizardLayout>
    );
  }

  // QuickBooks Connect Mode - DISABLED (requires Intuit app review)
  // This code is kept for future use when the integration is approved
  /*
  if (entryMode === 'quickbooks') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Connect QuickBooks Online</h1>
            <p className="text-gray-600">Import your financial data directly from QuickBooks</p>
          </div>

          <QuickBooksConnect
            onImportComplete={(importData) => {
              console.log('✅ QuickBooks import complete:', importData);
              
              // Update state with imported data
              if (importData.financial_data) {
                setAiExtractedData(importData.financial_data);
              }
              
              // Immediately update wizard state
              if (onDataUpdate) {
                onDataUpdate({
                  financial_data: importData.financial_data || {},
                  reconstructed_pl: importData.reconstructed_pl || {},
                  fromQuickBooks: true,
                  quickbooks_import_id: importData.import_id
                });
              }
              
              // Switch to completion view
              setEntryMode('quickbooks_complete');
            }}
            onError={(error) => {
              console.error('QuickBooks error:', error);
              setValidationError(`QuickBooks Error: ${error}`);
            }}
          />

          <div className="mt-8 text-center">
            <button
              onClick={() => setEntryMode(null)}
              className="text-gray-600 hover:text-gray-800 font-semibold"
            >
              ← Change Method
            </button>
          </div>
        </div>
      </div>
    );
  }
  */

  // QuickBooks Import Complete Mode
  if (entryMode === 'quickbooks_complete') {
    return (
      <WizardLayout
        title="QuickBooks Data Imported"
        subtitle="Your financial data has been imported successfully"
        showAutoSave={false}
      >
        <div className="space-y-4">
          {validationError && (
            <p className="text-red-500 text-sm">{validationError}</p>
          )}
          {/* Success Banner */}
          <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-100">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-green-700 font-medium">Import Successful</p>
              <p className="text-sm text-green-600">Financial data ready for review</p>
            </div>
          </div>

          {/* Data Summary */}
          {data.reconstructed_pl && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h2 className="text-base font-semibold mb-3">Financial Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">
                    ${(data.reconstructed_pl.totals?.revenue || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-xs text-gray-600">Revenue</div>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <div className="text-xl font-bold text-amber-600">
                    ${(data.reconstructed_pl.totals?.cogs || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-xs text-gray-600">COGS</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-600">
                    ${(data.reconstructed_pl.totals?.payroll || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-xs text-gray-600">Payroll</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-xl font-bold text-purple-600">
                    ${(data.reconstructed_pl.totals?.expenses || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-xs text-gray-600">Expenses</div>
                </div>
              </div>
            </div>
          )}

          {/* P&L Visualization */}
          {data.reconstructed_pl && (
            <div className="bg-gray-50 rounded-lg p-4">
              <PLVisualization
                financialData={data.financial_data || {}}
                reconstructedPL={data.reconstructed_pl}
                eventYear={eventStartDate ? new Date(eventStartDate).getFullYear().toString() : undefined}
              />
            </div>
          )}

          {/* Event Coverage Period */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-base font-semibold mb-3">📅 Event Coverage Period</h2>
            <EventCoveragePeriod
              financialData={data.financial_data}
              plFiles={[]}
              eventStartDate={eventStartDate}
              eventEndDate={eventEndDate}
              femaDeclarationNumber={femaDeclarationNumber}
              onDateChange={handleEventDateChange}
              businessAddress={data.business_address}
            />
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={() => setEntryMode('quickbooks')}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
            >
              ← Re-import Data
            </button>
            <button
              onClick={() => {
                if (!eventStartDate || !eventEndDate) {
                  setValidationError('Please enter the Event Coverage Period dates before continuing.');
                  return;
                }

                onNext({
                  financial_data: data.financial_data,
                  reconstructed_pl: data.reconstructed_pl,
                  fromQuickBooks: true,
                  quickbooks_import_id: data.quickbooks_import_id,
                  event_start_date: eventStartDate,
                  event_end_date: eventEndDate,
                  fema_declaration_number: femaDeclarationNumber,
                  coverage_year: new Date(eventStartDate).getFullYear()
                });
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              Continue to Step 4 →
            </button>
          </div>
        </div>
      </WizardLayout>
    );
  }

  // Cleanup Complete Mode - Show summary and continue
  if (entryMode === 'cleanup_complete') {
    const cleanupSummary = data.cleanup_summary || {};
    const financialYears = Object.keys(data.financial_data || {}).filter(k => k.startsWith('data_'));
    
    const handleCleanupContinue = () => {
      if (!eventStartDate || !eventEndDate) {
        setValidationError('Please enter the Event Coverage Period dates before continuing.');
        return;
      }

      const startDate = new Date(eventStartDate);
      const endDate = new Date(eventEndDate);
      if (endDate < startDate) {
        setValidationError('Event end date must be after start date');
        return;
      }
      
      onNext({ 
        method: 'cleanup',
        financial_data: data.financial_data,
        cleanup_job_id: data.cleanup_job_id,
        event_start_date: eventStartDate,
        event_end_date: eventEndDate,
        fema_declaration_number: femaDeclarationNumber,
        coverage_year: startDate.getFullYear()
      });
    };
    
    return (
      <WizardLayout
        title="Financial Data from BI Cleanup"
        subtitle="Your cleaned and validated financial data is ready to use"
        showAutoSave={false}
      >
        <div className="space-y-4">
          {validationError && (
            <p className="text-red-500 text-sm">{validationError}</p>
          )}
          {statusMessage && (
            <p className="text-green-600 text-sm">{statusMessage}</p>
          )}
          {/* Success Banner */}
          <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-100">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-green-700 font-medium">Cleanup Complete</p>
              <p className="text-sm text-green-600">Data validated and ready for review</p>
            </div>
          </div>

          {/* Cleanup Summary Stats */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-orange-500" />
              Cleanup Summary
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-xl font-bold text-blue-600">
                  {cleanupSummary.total_transactions || data.metadata?.transaction_count || '?'}
                </div>
                <div className="text-xs text-gray-600">Transactions</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-xl font-bold text-green-600">{financialYears.length}</div>
                <div className="text-xs text-gray-600">Years of Data</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-xl font-bold text-purple-600">
                  {cleanupSummary.categories_found?.length || '?'}
                </div>
                <div className="text-xs text-gray-600">Categories</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-xl font-bold text-orange-600">
                  {data.cleanup_job_id?.slice(0, 8) || 'N/A'}
                </div>
                <div className="text-xs text-gray-600">Job ID</div>
              </div>
            </div>
            
            {cleanupSummary.date_range && (
              <div className="mt-3 text-sm text-gray-600 text-center">
                Date Range: {cleanupSummary.date_range.start} to {cleanupSummary.date_range.end}
              </div>
            )}
            
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Years Available:</p>
              <div className="flex flex-wrap gap-2">
                {financialYears.map(yearKey => (
                  <span key={yearKey} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {yearKey.replace('data_', '')}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Data Gap Detector */}
          <DataGapDetector
            financialData={data.financial_data || {}}
            reconstructedPL={data.reconstructed_pl}
            eventStartDate={data.event_start_date || eventStartDate}
            eventEndDate={data.event_end_date || eventEndDate}
          />

          {/* Reconstructed P&L Review */}
          {cleanupSummary.reconstruction && (
            <div className="bg-gray-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold">📊 Reconstructed P&L</h2>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  cleanupSummary.reconstruction.confidence === 'high' 
                    ? 'bg-green-100 text-green-700'
                    : cleanupSummary.reconstruction.confidence === 'medium'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {cleanupSummary.reconstruction.confidence === 'high' ? '✅ High' : 
                   cleanupSummary.reconstruction.confidence === 'medium' ? '⚠️ Medium' : '⚠️ Low'} Confidence
                </span>
              </div>
              
              {/* Data Sources */}
              {cleanupSummary.reconstruction.sources && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {cleanupSummary.reconstruction.sources.includes('bank') && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">🏦 Bank Statements</span>
                  )}
                  {cleanupSummary.reconstruction.sources.includes('credit_card') && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">💳 Credit Card</span>
                  )}
                </div>
              )}
              
              {/* Assumptions */}
              {cleanupSummary.reconstruction.assumptions?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <h4 className="font-semibold text-amber-900 mb-2 text-sm">
                    📋 Estimates & Assumptions ({cleanupSummary.reconstruction.assumptions.length})
                  </h4>
                  <ul className="space-y-1">
                    {cleanupSummary.reconstruction.assumptions.slice(0, 3).map((assumption: any, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-amber-800">
                        <span className="text-amber-500">⚡</span>
                        <span>{assumption.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Editable P&L Table */}
              {data.reconstructed_pl?.by_year && (
                <EditablePLTable
                  data={data.reconstructed_pl}
                  assumptions={cleanupSummary.reconstruction?.assumptions || []}
                  onChange={(updatedPL, overrides) => {
                    if (onDataUpdate) {
                      onDataUpdate({ ...data, reconstructed_pl: updatedPL, user_pl_overrides: overrides });
                    }
                    setUserPLOverrides(overrides);
                  }}
                />
              )}
              
              {/* Override Option */}
              <div className="mt-3 flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-blue-900">💡 Upload actual P&L for higher accuracy</p>
                </div>
                <label className="cursor-pointer px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg">
                  <input
                    type="file"
                    accept=".pdf,.xlsx,.xls,.csv"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        onDataChange({ ...data, data_source_override: 'actual_pl', data_source_verified: true, override_file_name: file.name });
                        setStatusMessage(`"${file.name}" selected.`);
                      }
                    }}
                  />
                  📄 Upload P&L
                </label>
              </div>
              
              {data.data_source_override && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  ✅ <strong>Actual P&L Selected:</strong> {data.override_file_name}
                </div>
              )}
            </div>
          )}

          {/* P&L Visualization */}
          {(data.reconstructed_pl?.by_year || data.financial_data) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <PLVisualization
                financialData={data.financial_data || {}}
                reconstructedPL={data.reconstructed_pl}
                industryBenchmarks={{
                  cogs_pct: cleanupSummary.reconstruction?.assumptions?.find((a: any) => a.field === 'cogs')?.industry_pct || 35,
                  payroll_pct: cleanupSummary.reconstruction?.assumptions?.find((a: any) => a.field === 'payroll')?.industry_pct || 28,
                  expenses_pct: cleanupSummary.reconstruction?.assumptions?.find((a: any) => a.field === 'expenses')?.industry_pct || 15
                }}
                eventYear={eventStartDate ? new Date(eventStartDate).getFullYear().toString() : undefined}
              />
            </div>
          )}

          {/* Event Coverage Period */}
          <div className="bg-gray-50 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold">📅 Event Coverage Period</h2>
              {(!eventStartDate || !eventEndDate) && (
                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">Required</span>
              )}
            </div>
            
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
              <strong>💡 Note:</strong> Enter the date range of the <strong>disruption event</strong>, not your financial document dates.
            </div>
            
            <EventCoveragePeriod
              financialData={data.financial_data}
              plFiles={[]}
              eventStartDate={eventStartDate}
              eventEndDate={eventEndDate}
              femaDeclarationNumber={femaDeclarationNumber}
              onDateChange={handleEventDateChange}
              businessAddress={data.business_address}
            />
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={() => setEntryMode(null)}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
            >
              ← Change Method
            </button>
            <button
              onClick={handleCleanupContinue}
              disabled={!eventStartDate || !eventEndDate}
              className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
                eventStartDate && eventEndDate
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue to Step 4 →
            </button>
          </div>
        </div>
      </WizardLayout>
    );
  }

  return null;
}
